import Modal from 'components/modal'
import { IAdminAuditLog } from 'types/adminAuditLog'
import { CustomObject } from 'types/common'

export interface AuditChangedField {
  field: string
  before: CustomObject
  after: CustomObject
}

function renderValue(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (Array.isArray(value))
    return value.length === 0 ? '[ ]' : JSON.stringify(value)
  if (typeof value === 'object') return JSON.stringify(value, null, 2)
  return String(value)
}

function ChangeRow({
  change,
  index,
}: {
  change: AuditChangedField
  index: number
}) {
  const isCreate = change.before === null
  const isDelete = change.after === null

  return (
    <tr className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
      <td className="py-3 pl-4 pr-3 align-top">
        <span className="inline-block rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">
          {change.field}
        </span>
      </td>
      <td className="px-3 py-3 align-top">
        {isCreate ? (
          <span className="text-xs italic text-gray-400">new field</span>
        ) : (
          <span
            className={`block whitespace-pre-wrap break-all rounded px-2 py-1 font-mono text-xs
              ${
                isDelete
                  ? 'bg-red-50 text-red-700'
                  : 'bg-gray-100 text-gray-700'
              }`}
          >
            {renderValue(change.before)}
          </span>
        )}
      </td>
      <td className="px-2 py-3 text-center align-middle text-sm text-gray-300">
        →
      </td>
      <td className="px-3 py-3 align-top">
        {isDelete ? (
          <span className="text-xs italic text-gray-400">deleted</span>
        ) : (
          <span
            className={`block whitespace-pre-wrap break-all rounded px-2 py-1 font-mono text-xs
              ${
                isCreate
                  ? 'bg-green-50 text-green-700'
                  : 'bg-blue-50 text-blue-700'
              }`}
          >
            {renderValue(change.after)}
          </span>
        )}
      </td>
    </tr>
  )
}

interface AuditLogChangesModalProps {
  log: IAdminAuditLog
  isOpen: boolean
  onClose: () => void
}

const AuditLogChangesModal = ({
  log,
  isOpen,
  onClose,
}: AuditLogChangesModalProps) => {
  const changesCount = log?.changes?.length ?? 0

  const title = log ? `${log.action} · ${log.module}` : ''

  return (
    <Modal
      isOpen={isOpen}
      closeModal={onClose as React.MouseEventHandler<HTMLSpanElement>}
      title={title}
      titleClassname="capitalize"
      width={760}
    >
      <>
        {/* Entity + user meta */}
        {log && (log.entityLabel || log.entityId) && (
          <div className="mb-4 flex items-center justify-between px-6">
            <div className="flex items-center gap-1.5 text-xs text-gray-400">
              {log.entityLabel && (
                <span className="font-medium text-gray-600">
                  {log.entityLabel}
                </span>
              )}
              {log.entityLabel && log.entityId && <span>·</span>}
              {log.entityId && (
                <span className="font-mono">{log.entityId}</span>
              )}
            </div>
          </div>
        )}

        {/* Changes table */}
        {changesCount === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <svg
              className="mb-3 h-10 w-10 opacity-40"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <p className="text-sm">No field changes recorded</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-y border-gray-100 bg-gray-50 text-left text-xs font-medium uppercase tracking-wide text-gray-400">
                <th className="w-40 py-2.5 pl-4 pr-3">Field</th>
                <th className="px-3 py-2.5">Before</th>
                <th className="w-8" />
                <th className="px-3 py-2.5">After</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {log?.changes.map((change: AuditChangedField, i: number) => (
                <ChangeRow key={change.field} change={change} index={i} />
              ))}
            </tbody>
          </table>
        )}

        {log?.metadata && (
          <div>
            <p className="mt-4 py-2.5 text-xs font-bold uppercase tracking-wide text-gray-400">
              Meta Data
            </p>
            <pre className="rounded-lg bg-gray-50 px-4 py-3 text-xs text-gray-500">
              {JSON.stringify(log.metadata, null, 2)}
            </pre>
          </div>
        )}

        {/* Footer */}
        <div className="mt-2 flex items-center justify-between border-t border-gray-100 px-6 py-3 text-xs text-gray-400">
          <span>
            {changesCount > 0
              ? `${changesCount} field${changesCount > 1 ? 's' : ''} changed`
              : 'Event log'}{' '}
            by {log?.user?.name ?? 'Unknown user'}
          </span>
          {log?.createdAt && (
            <span className="font-bold">
              {new Date(log.createdAt).toLocaleString()}
            </span>
          )}
        </div>
      </>
    </Modal>
  )
}

export default AuditLogChangesModal
